import React from 'react';
import PropTypes from 'prop-types';
// https://www.npmjs.com/package/moment-business-days
import { FormSection } from 'AppSrc/core/components/forms/form-section';
import { prefixStringWithIndexPlusOne } from 'AppSrc/core/utility-functions/string-fun';
import { navigationPropsShape } from 'AppSrc/core/components/forms/section-nav';
import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
import {
  FIXED,
  MOBILE,
  IMPACT_LEVEL_1,
  IMPACT_LEVEL_2,
} from 'AppSrc/core/components/forms/value-dependency-constants';
import { PEER_APPROVED_TYPE } from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-constants';
// Key values that are repeated a lot
import { defaultFieldLogic } from 'AppSrc/core/components/forms/form-base-components/data-structure/default-field-logic';
import { getWiiMetadata } from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-logic';
import {
  formDataShape,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/v-f-proptypes';
import {
  START_AND_END_DATE,
  SECTION_TITLES,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
// common logic across this form
import {
  isCrqExpedited,
  getRiskValue,
  getImpactLevel,
  currentSelectedNetwork,
  getIsOlaCrq,
  getIsInternationalCrq,
  isCrqEmergency,
} from '../common-logic';
// specific logic to this form
import {
  getInitialDate,
  getExcludeDates,
  getMinDate,
  getMaxDate,
  getEmergencyMaxDate,
} from './section-three-data-layer-functions';
// form fields
import { scheduledStartDate } from './scheduled-start-date';
import { scheduledEndDate } from './scheduled-end-date';
import { OwTitle } from './ow-title';
import { ScheduledTitle } from './scheduled-date-title';
import { owStartDate } from './ow-start-date';
import { owEndDate } from './ow-end-date';
import { OWScheduledStartDate } from './ow-scheduled-start-date';
import { OWScheduledEndDate } from './ow-scheduled-end-date';


// ssts and paragraphs
import { paragraphBasedUponThe } from './paragraph-based-upon-the';
import { sstScheduledStartDate } from './sst-scheduled-start-date';
import { sstScheduledEndDate } from './sst-scheduled-end-date';
import { ScheduledChangeStartDate } from './sc-start-date';
import { ScheduledChangeEndDate } from './sc-end-date';



// Section ID (for the data layer)
const sectionId = START_AND_END_DATE;
// Title for the main form
const sectionTitle = SECTION_TITLES[START_AND_END_DATE];

const hasEmergenctL3Approvalwii = (formData, selectedNetwork) => {
  if (!formData) { return false; }
  const wiis = getWiiMetadata(formData);
  const approvalType = PEER_APPROVED_TYPE[selectedNetwork];
  return wiis.some((wii) => wii.type === approvalType);
};

// callback for the form section to figure out what fields to render with conditional logic
const getCurrentFields = (params) => {

  const {
    // values = {},
    formData = {},
    // touched = {},
    // userRoles,
  } = params;


  // const isOloCrq = getIsOlaCrq(formData);
  const changeDetails = 'CHANGE_DETAILS';
  const scheduledDate = 'scheduled-start-date';
  const changeQuestions = 'CHANGE_QUESTIONS';
  const isOloByService = formData.dataByFormSectionId[changeDetails] ? formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: Other Licensed Operator (OLO)' || formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: International Other Licensed Operator (OLO)' : null;
  const isOloCrq = getIsOlaCrq(formData) || isOloByService;
  const selectedNetwork = currentSelectedNetwork(formData);
  const isInternationalCrq = getIsInternationalCrq(formData);
  const slaType = formData.dataByFormSectionId[changeQuestions].dataByFieldId.timing.value[0];
  const statusReasonType = formData.statusReason;
  const isCrqPostponed = statusReasonType === 'PostponedCRQ' || statusReasonType === '30300';

  // We are determining here whether CRQ is emergency or not based on timing value and postponed status reason
  const isEmergencyCrq = ((slaType === 'Emergency' || slaType === '2000') && isCrqPostponed) || hasEmergenctL3Approvalwii(formData, selectedNetwork);
  const scheduledStartValue = params.values ? params.values[scheduledDate] : null;


  // MOBILE lacks an impactLevel and FIXED lacks a riskValue. Both values are used for similar things though
  const riskValue = selectedNetwork === MOBILE ? getRiskValue(formData) : null;
  const impactLevel = selectedNetwork === FIXED ? getImpactLevel(formData) : null;

  const isExpedited = isCrqExpedited(formData);
  const isEmergency = isCrqEmergency(formData);
  let isReadonly = false;
  if (isEmergency) {
    if (isEmergencyCrq) {
      isReadonly = true;
    }
  }

  const currentFields = [];
  currentFields.push({
    ...defaultFieldLogic,
    componentData: paragraphBasedUponThe,
  });
  if (selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2)) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: ScheduledTitle,
    });
  }
  if (selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2)) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: ScheduledChangeStartDate,
    });
  }
  else {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: sstScheduledStartDate,
    });
  }

  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...scheduledStartDate,
      initialValues: getInitialDate({
        selectedNetwork,
        riskValue,
        impactLevel,
        isExpedited,
        isEmergency,
        isOloCrq,
        isInternationalCrq,
      }),
      vFParams: {
        ...scheduledStartDate.vFParams,
        minDate: getMinDate({
          selectedNetwork,
          riskValue,
          impactLevel,
          isExpedited,
          isEmergency,
          isOloCrq,
          isInternationalCrq,
        }),
        // the following two options aren't needed (yet)
        excludeDates: getExcludeDates(),
        // the following getEmergencyMaxDate fucntion for emeregncy end date to greout thedates after end date.
        maxDate: getEmergencyMaxDate({
          selectedNetwork,
          isEmergency,
          isOloCrq,
          isInternationalCrq,
        }),
        selectedNetwork,
      },
    },
    // We are making schedule start date readonly and unclickable for 2nd journey of L3 Approver.
    readOnly: isReadonly,
    disabledFieldOptions: {
      disabled: isReadonly,
      disabledValue: scheduledStartValue,
      unDisabledValue: null,
    },
  });


  if (selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2)) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: ScheduledChangeEndDate,
    });
  }
  else {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: sstScheduledEndDate,
    });
  }

  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...scheduledEndDate,
      initialValues: null,
      vFParams: {
        ...scheduledEndDate.vFParams,
        minDate: getMinDate({
          selectedNetwork,
          riskValue,
          impactLevel,
          isExpedited,
          isEmergency,
          isOloCrq,
          isInternationalCrq,
        }),
        // the following two options aren't needed (yet)
        excludeDates: getExcludeDates(),
        maxDate: getMaxDate(),
        selectedNetwork,
      },
    },
  });
  if (selectedNetwork === FIXED && (impactLevel === IMPACT_LEVEL_1 || impactLevel === IMPACT_LEVEL_2) && !isOloCrq) {
    currentFields.push({
      ...defaultFieldLogic,
      componentData: OwTitle,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: OWScheduledStartDate,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...owStartDate,
        initialValues: null,
        vFParams: {
          ...owStartDate.vFParams,
          minDate: getMinDate({
            selectedNetwork,
            riskValue,
            impactLevel,
            isExpedited,
            isEmergency,
            isOloCrq,
            isInternationalCrq,
          }),
          // the following two options aren't needed (yet)
          excludeDates: getExcludeDates(),
          maxDate: scheduledEndDate.getEndDate(),
          selectedNetwork,
        },
      },
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: OWScheduledEndDate,
    });
    currentFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...owEndDate,
        initialValues: null,
        vFParams: {
          ...owEndDate.vFParams,
          minDate: getMinDate({
            selectedNetwork,
            riskValue,
            impactLevel,
            isExpedited,
            isEmergency,
            isOloCrq,
            isInternationalCrq,
          }),
          // the following two options aren't needed (yet)
          excludeDates: getExcludeDates(),
          maxDate: scheduledEndDate.getEndDate(),
          selectedNetwork,
        },
      },
    });
  }
  return currentFields;
};

// generates the formSection!
const SectionThree = React.memo(({
  handleSuccessfulSubmit,
  userRoles,
  formData,
  navigationProps,
  sectionIndex,
  editingStatus,
}) => {
  // Props for the SectionNav in FormSection. these come from the parent but we mutate them to be relivent to the section
  const filteredNavigationProps = {
    ...navigationProps,
    submitForm: null,
  };

  const formattedSectionTitle = prefixStringWithIndexPlusOne(sectionTitle, sectionIndex);

  return (
    <FormSection
      sectionTitle={formattedSectionTitle}
      sectionId={sectionId}
      getCurrentFields={getCurrentFields}
      userRoles={userRoles}
      formData={formData}
      navigationProps={filteredNavigationProps}
      handleSuccessfulSubmit={handleSuccessfulSubmit}
      editingStatus={editingStatus}
    />
  );
});


SectionThree.propTypes = {
  handleSuccessfulSubmit: PropTypes.func,
  userRoles: userRolesPropShape,
  formData: formDataShape,
  navigationProps: navigationPropsShape,
  sectionIndex: PropTypes.number,
  editingStatus: PropTypes.string,
};

export {
  SectionThree,
  getCurrentFields,
  START_AND_END_DATE,
};
